import { ImageUploadDropzone } from '@/components/shared/ImageUploadDropzone';
import { EntityFormShell, useEntityForm } from '@startupquickstart/frontend';
import type { AmenityType } from '@/config/objects/amenity-types';
import { cn } from '@/lib/utils';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  FormFieldGroup,
  Input,
  RichTextEditor,
  toast,
} from '@startupquickstart/frontend';
import { useNavigate, useParams } from 'react-router-dom';

interface AmenityTypeFormData extends Record<string, unknown> {
  name: string;
  description: string;
  featuredImageId: string | undefined;
}

const DEFAULTS: AmenityTypeFormData = {
  name: '',
  description: '',
  featuredImageId: undefined,
};

export function AmenityTypeFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const itemId = id === 'new' ? undefined : id;
  const isEdit = !!itemId;

  const form = useEntityForm<AmenityTypeFormData, AmenityType>({
    resource: 'amenity-types',
    itemId,
    defaults: DEFAULTS,
    queryKey: 'AmenityType',
    fromApi: (a) => ({
      name: a.name ?? '',
      description: a.description ?? '',
      featuredImageId: a.featuredImageId ?? undefined,
    }),
    toApi: (f) => ({
      name: f.name.trim(),
      description: f.description || null,
      featuredImageId: f.featuredImageId || null,
    }),
    validate: (f) => (f.name.trim() ? {} : { name: 'Name is required' }),
    onSuccess: (entity, { isEdit: wasEdit }) => {
      if (wasEdit) {
        toast.success('Amenity type saved successfully');
        navigate(-1);
      } else {
        toast.success('Amenity type created successfully');
        if (entity.id) navigate(`/amenity-types/${entity.id}`);
      }
    },
    onNotFound: () => {
      toast.error('Amenity type not found');
      navigate('/amenity-types');
    },
  });

  return (
    <EntityFormShell
      title={isEdit ? 'Edit Amenity Type' : 'Create Amenity Type'}
      subtitle={
        isEdit
          ? form.formData.name || 'Update amenity type details'
          : 'Add a new amenity type to the platform'
      }
      backHref={isEdit && itemId ? `/amenity-types/${itemId}` : '/amenity-types'}
      form={form}
    >
      <div className="space-y-6">
        <Card className="rounded-xl border bg-card shadow-sm">
          <CardHeader className="px-6 py-4 border-b">
            <CardTitle className="text-zinc-900">General Information</CardTitle>
            <CardDescription>Core amenity type details</CardDescription>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            <FormFieldGroup name="name" label="Name" required error={form.errors.name}>
              <Input
                id="name"
                value={form.formData.name}
                onChange={(e) => form.updateField('name', e.target.value)}
                placeholder="Enter amenity type name"
                className={cn(form.errors.name && 'border-destructive')}
              />
            </FormFieldGroup>

            <ImageUploadDropzone
              targetAspectRatio={4 / 3}
              value={form.formData.featuredImageId}
              onChange={(value) => form.updateField('featuredImageId', value)}
            />

            <FormFieldGroup name="description" label="Description">
              <RichTextEditor
                value={form.formData.description}
                onChange={(value) => form.updateField('description', value ?? '')}
              />
            </FormFieldGroup>
          </CardContent>
        </Card>
      </div>
    </EntityFormShell>
  );
}
